/**
 * NewInvestmentPage Component
 *
 * Page for creating a new investment
 */

import { usePortcos, useSegments, useCreateInvestment } from '@medsuite/api-client';
import {
  Input,
  Select,
  TextArea,
  MarkdownEditor,
  Button,
  Card,
  Alert,
  Checkbox,
  DatePicker,
  useDialog,
} from '@medsuite/ui-components';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InvestmentImporter } from './components/InvestmentImporter';

export const NewInvestmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: portcos = [] } = usePortcos();
  const { data: segments = [] } = useSegments();
  const createInvestment = useCreateInvestment({
    onSuccess: (investment) => {
      // Navigate to the edit page for the newly created investment
      navigate(`/investments/${investment.investment_id}/edit`);
    },
  });

  const [formData, setFormData] = useState({
    title: '',
    portco_id: null as number | null,
    segment_id: null as number | null,
    submitted_date: new Date().toISOString().split('T')[0],
    is_regulatory_requirement: false,
    regulatory_details: '',
    prime_objective: '',
    problem_context: '',
    improvement_ideas: '',
    assumptions: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showImporter, setShowImporter] = useState(false);

  // Dialog hooks
  const { confirm } = useDialog();

  const handleFieldChange = (field: string, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.portco_id) {
      newErrors.portco_id = 'Portfolio Company is required';
    }
    if (!formData.segment_id) {
      newErrors.segment_id = 'Segment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createInvestment.mutateAsync({
        title: formData.title,
        portco_id: formData.portco_id!,
        segment_id: formData.segment_id!,
        is_regulatory_requirement: formData.is_regulatory_requirement,
        regulatory_requirement_details: formData.is_regulatory_requirement
          ? formData.regulatory_details
          : undefined,
        prime_objective: formData.prime_objective || undefined,
        problem_context: formData.problem_context || undefined,
        improvement_ideas: formData.improvement_ideas || undefined,
        assumptions: formData.assumptions || undefined,
      });
    } catch (error) {
      console.error('Failed to create investment:', error);
    }
  };

  const handleCancel = async () => {
    if (
      Object.values(formData).some((value) => value !== '' && value !== null && value !== false)
    ) {
      const confirmCancel = await confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to cancel?',
        variant: 'warning',
        confirmText: 'Discard Changes',
      });
      if (!confirmCancel) {
        return;
      }
    }
    navigate('/');
  };

  const portcoOptions = portcos
    .map((p) => ({
      value: p.portco_id,
      label: p.portco_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const segmentOptions = segments
    .map((s) => ({
      value: s.segment_id,
      label: s.segment_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Create New Investment
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Fill in the basic information to create a new investment. You can add more details
              after creation.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          {createInvestment.isError && (
            <Alert variant="error" className="mb-6">
              <p className="font-semibold">Failed to create investment</p>
              <p className="text-sm">
                {(createInvestment.error as Error)?.message || 'An error occurred'}
              </p>
            </Alert>
          )}

          <div className="space-y-8">
            {/* Basic Information */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <Input
                    label="Investment Title"
                    value={formData.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Enter a descriptive title for this investment"
                    required
                    error={errors.title}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Portfolio Company"
                      options={portcoOptions}
                      value={formData.portco_id ?? undefined}
                      onChange={(value) => handleFieldChange('portco_id', value)}
                      placeholder="Select portfolio company"
                      required
                      error={errors.portco_id}
                    />

                    <Select
                      label="Segment"
                      options={segmentOptions}
                      value={formData.segment_id ?? undefined}
                      onChange={(value) => handleFieldChange('segment_id', value)}
                      placeholder="Select segment"
                      required
                      error={errors.segment_id}
                    />
                  </div>

                  <DatePicker
                    label="Submitted Date"
                    value={formData.submitted_date}
                    onChange={(e) => handleFieldChange('submitted_date', e.target.value)}
                  />
                </div>
              </div>
            </Card>

            {/* Regulatory Information */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Regulatory Information
                </h3>
                <div className="space-y-4">
                  <Checkbox
                    label="Is this a regulatory requirement?"
                    checked={formData.is_regulatory_requirement}
                    onChange={(e) =>
                      handleFieldChange('is_regulatory_requirement', e.target.checked)
                    }
                  />

                  {formData.is_regulatory_requirement && (
                    <TextArea
                      label="Regulatory Details"
                      value={formData.regulatory_details}
                      onChange={(e) => handleFieldChange('regulatory_details', e.target.value)}
                      placeholder="Describe the regulatory requirements..."
                      rows={4}
                    />
                  )}
                </div>
              </div>
            </Card>

            {/* Prime Objective (Optional) */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Prime Objective{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    (Optional)
                  </span>
                </h3>
                <MarkdownEditor
                  label="Prime Objective"
                  value={formData.prime_objective}
                  onChange={(value) => handleFieldChange('prime_objective', value)}
                  placeholder="What is the primary objective of this investment?"
                  rows={4}
                  helperText="Supports markdown formatting (bold, lists, etc.). You can add more details after creating the investment."
                />
              </div>
            </Card>

            {/* Problem & Context (Optional) */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Problem & Context{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    (Optional)
                  </span>
                </h3>
                <MarkdownEditor
                  label="Problem Context"
                  value={formData.problem_context}
                  onChange={(value) => handleFieldChange('problem_context', value)}
                  placeholder="Describe the problem and context..."
                  rows={6}
                  helperText="Supports markdown formatting. You can add more details after creating the investment."
                />
              </div>
            </Card>

            {/* Improvement Ideas (Optional) */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Improvement Ideas{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    (Optional)
                  </span>
                </h3>
                <MarkdownEditor
                  label="Improvement Ideas"
                  value={formData.improvement_ideas}
                  onChange={(value) => handleFieldChange('improvement_ideas', value)}
                  placeholder="What are the proposed improvements?"
                  rows={6}
                  helperText="Supports markdown formatting. You can add more details after creating the investment."
                />
              </div>
            </Card>

            {/* Assumptions & Constraints (Optional) */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Assumptions & Constraints{' '}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    (Optional)
                  </span>
                </h3>
                <MarkdownEditor
                  label="Assumptions & Constraints"
                  value={formData.assumptions}
                  onChange={(value) => handleFieldChange('assumptions', value)}
                  placeholder="List key assumptions and constraints..."
                  rows={6}
                  helperText="Supports markdown formatting. You can add more details after creating the investment."
                />
              </div>
            </Card>
          </div>

          {/* Form Actions */}
          <div className="sticky bottom-0 mt-8 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg rounded-lg">
            <div className="flex justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowImporter(true)}
                disabled={createInvestment.isPending}
              >
                Import from Excel
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={createInvestment.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createInvestment.isPending}>
                  {createInvestment.isPending ? 'Creating...' : 'Create Investment'}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* Import Modal */}
        <InvestmentImporter
          isOpen={showImporter}
          onClose={() => setShowImporter(false)}
          onImportComplete={(result) => {
            if (result.success > 0) {
              // Navigate to investments list after successful import
              navigate('/');
            }
          }}
        />
      </div>
    </div>
  );
};

export default NewInvestmentPage;
